import React, { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  ConnectionLineType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from '@/providers/ThemeProvider';
import { ENTITY_TYPE_COLORS, getEntityColor } from '@/lib/utils/entityColors';
import type { HierarchicalNode, HierarchicalLink } from '@/types/relationshipDetails';

interface FieldLevelGraphProps {
  nodes: HierarchicalNode[];
  links: HierarchicalLink[];
  width?: number;
  height?: number;
  onNodeClick?: (node: HierarchicalNode) => void;
  highlightedNodeId?: string;
}

/**
 * FieldLevelGraph Component
 * 
 * Visualizes field-level connections between entities using React Flow
 * Shows detailed node-to-node, field-to-field relationships
 */
export const FieldLevelGraph: React.FC<FieldLevelGraphProps> = ({
  nodes: hierarchicalNodes,
  links: hierarchicalLinks,
  width = 1200,
  height = 800,
  onNodeClick,
  highlightedNodeId,
}) => {
  const { effectiveTheme } = useTheme();
  const isDarkMode = effectiveTheme === 'dark';

  // Convert hierarchical nodes to React Flow nodes
  const flowNodes: Node[] = useMemo(() => {
    return hierarchicalNodes.map((node, index) => {
      const color = getEntityColor(node.type);
      const isHighlighted = node.id === highlightedNodeId;
      
      return {
        id: node.id,
        type: 'default',
        position: {
          x: (index % 5) * 250,
          y: Math.floor(index / 5) * 150,
        },
        data: {
          label: node.name,
        },
        style: {
          background: isHighlighted ? color : (isDarkMode ? '#1f2937' : '#ffffff'),
          border: `2px solid ${color}`,
          borderRadius: '8px',
          padding: '10px',
          fontSize: '12px',
          width: 200,
          color: isHighlighted ? '#ffffff' : (isDarkMode ? '#e5e7eb' : '#1f2937'),
          fontWeight: isHighlighted ? 'bold' : 'normal',
        },
      };
    });
  }, [hierarchicalNodes, isDarkMode, highlightedNodeId]);

  // Convert hierarchical links to React Flow edges
  const flowEdges: Edge[] = useMemo(() => {
    return hierarchicalLinks.map((link, index) => {
      const relationshipTypeColors: Record<string, string> = {
        navigation: '#3b82f6', // Blue
        speaker: '#8b5cf6',    // Purple
        linked_value: '#f97316', // Orange
        template: '#10b981',   // Green
        reference: '#64748b',  // Slate
        unknown: '#6b7280',    // Gray
      };

      const color = relationshipTypeColors[link.relationshipType] || relationshipTypeColors.unknown;
      
      return {
        id: `${link.source}-${link.target}-${index}`,
        source: link.source,
        target: link.target,
        type: 'smoothstep',
        animated: link.relationshipType === 'navigation',
        label: link.relationshipType.replace('_', ' '),
        labelStyle: {
          fontSize: 10,
          fill: isDarkMode ? '#9ca3af' : '#6b7280',
        },
        labelBgStyle: {
          fill: isDarkMode ? '#1f2937' : '#ffffff',
          fillOpacity: 0.8,
        },
        style: {
          stroke: color,
          strokeWidth: 2,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: color,
        },
      };
    });
  }, [hierarchicalLinks, isDarkMode]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Update nodes when props change
  React.useEffect(() => {
    setNodes(flowNodes);
  }, [flowNodes, setNodes]);

  // Update edges when props change
  React.useEffect(() => {
    setEdges(flowEdges);
  }, [flowEdges, setEdges]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (onNodeClick) {
        const hierarchicalNode = hierarchicalNodes.find((n) => n.id === node.id);
        if (hierarchicalNode) {
          onNodeClick(hierarchicalNode);
        }
      }
    },
    [onNodeClick, hierarchicalNodes]
  );

  return (
    <div style={{ width, height }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        minZoom={0.1}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Background
          color={isDarkMode ? '#4b5563' : '#e5e7eb'}
          gap={16}
        />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const hierarchicalNode = hierarchicalNodes.find((n) => n.id === node.id);
            return hierarchicalNode ? getEntityColor(hierarchicalNode.type) : '#6b7280';
          }}
          maskColor={isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'}
        />
        
        <Panel position="top-right" className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <div className="text-xs font-semibold mb-2">Relationship Types</div>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-blue-500"></div>
              <span>Navigation</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-purple-600"></div>
              <span>Speaker</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-orange-500"></div>
              <span>Linked Value</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-green-600"></div>
              <span>Template</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-slate-600"></div>
              <span>Reference</span>
            </div>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};
